import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {doc, getFirestore, onSnapshot} from 'firebase/firestore';
import {getSLPostSectionSurveyDocumentPath} from '@cip/core/firebase';
import {useCourseId} from 'hooks/router/useUrlParams';
import {getUserId} from 'hooks/user/useUserId';
import {
  isSLDebriefSubmitted,
  type SectionDebriefTarget,
} from './sectionDebriefRoadmapItem';

/** Return shape of {@link useSectionDebriefProgress}. */
export interface SectionDebriefProgress {
  /**
   * True once every requested debrief document has been read (a failed read
   * counts as read and not submitted). False while any is loading and when
   * the hook is disabled; true at once for an empty list.
   */
  isLoaded: boolean;
  /**
   * Whether the signed-in SL submitted the debrief for a section week. False
   * for a target that was not requested or has not loaded.
   */
  isSubmitted: (target: SectionDebriefTarget) => boolean;
}

const toKey = ({sectionId, sectionWeek}: SectionDebriefTarget) =>
  JSON.stringify([sectionId, sectionWeek]);

/**
 * Subscribes to the signed-in SL's debrief document
 * (`slDebriefs/{uid}` under the section week) for each requested section
 * week, so roadmap "Section Debrief" rows can show completion. Read-only and
 * reactive.
 *
 * @param params.isEnabled - When false, nothing is read and `isLoaded` stays
 *   false. Omitted means the reads run.
 * @param params.targets - The section weeks to watch. Order, duplicates and
 *   array identity do not matter.
 * @return Whether every read has landed, and a submitted lookup.
 * @throws Never; a failed read is logged and reads as not submitted.
 */
export function useSectionDebriefProgress({
  isEnabled = true,
  targets,
}: {
  isEnabled?: boolean;
  targets: readonly SectionDebriefTarget[];
}): SectionDebriefProgress {
  const courseId = useCourseId();
  const userId = getUserId();
  const keys = Array.from(new Set(targets.map(toKey))).sort();
  // A string, so the subscription follows the targets' contents rather than
  // the array's identity.
  const targetsKey = JSON.stringify(keys);
  const subscriptionKey = isEnabled && userId ? `${userId}|${targetsKey}` : '';
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  const [loaded, setLoaded] = useState<{
    subscriptionKey: string;
    submittedByKey: Record<string, boolean>;
  }>({subscriptionKey: '', submittedByKey: {}});

  useEffect(() => {
    if (!isEnabled || !userId) return;
    const db = getFirestore();
    const record = (key: string, isSubmitted: boolean) =>
      setLoaded(previous => ({
        subscriptionKey,
        submittedByKey: {
          ...(previous.subscriptionKey === subscriptionKey
            ? previous.submittedByKey
            : {}),
          [key]: isSubmitted,
        },
      }));
    const targetsByKey = new Map(
      targetsRef.current.map(target => [toKey(target), target]),
    );
    const unsubscribes = Array.from(targetsByKey, ([key, target]) =>
      onSnapshot(
        doc(
          db,
          getSLPostSectionSurveyDocumentPath({courseId, userId, ...target}),
        ),
        snapshot =>
          record(
            key,
            isSLDebriefSubmitted({
              exists: snapshot.exists(),
              data: snapshot.data(),
            }),
          ),
        error => {
          console.error(`[section debrief] read failed for ${key}`, error);
          record(key, false);
        },
      ),
    );
    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
  }, [courseId, isEnabled, subscriptionKey, targetsKey, userId]);

  const submittedByKey = useMemo(
    () =>
      loaded.subscriptionKey === subscriptionKey ? loaded.submittedByKey : {},
    [loaded, subscriptionKey],
  );
  const isSubmitted = useCallback(
    (target: SectionDebriefTarget) => submittedByKey[toKey(target)] === true,
    [submittedByKey],
  );
  const isLoaded =
    subscriptionKey !== '' && keys.every(key => key in submittedByKey);
  return {isLoaded, isSubmitted};
}
